import AxiosService from '../AxiosService';

export interface ScheduledProposalUnavailableItem {
  sku: string;
  quantity: number;
  /** FORA_DO_CATALOGO | SEM_ESTOQUE */
  reason: string;
  description: string | null;
  /** A segunda metade do nome do produto (embalagem, tamanho). Nulo em
   * `FORA_DO_CATALOGO`: não houve linha de catálogo para ler. */
  complemento: string | null;
}

export interface ScheduledProposal {
  proposalId: string;
  /** Dia de entrega programado pelo vendedor (YYYY-MM-DD). */
  scheduledDate: string;
  /** Itens já resolvidos contra o catálogo do cliente, com `proposal_qtd`. */
  items: any[];
  /** Itens da proposta que o catálogo não atende hoje. */
  unavailable: ScheduledProposalUnavailableItem[];
}

/**
 * PED-13 / CB-2 — proposta de pedido programado pendente do cliente.
 *
 * Devolve `null` quando não há proposta na janela de aceite (desde a criação
 * pelo CS até a data programada): é resposta normal, não erro.
 */
const GetScheduledProposal = async (
  clientCode: string,
): Promise<ScheduledProposal | null> => {
  const instance = AxiosService.createProductsAxiosInstance();
  const response = await instance.get('/client/scheduled-order', {
    headers: {
      'client-code': clientCode,
    },
  });

  return response.data?.data ?? null;
};

// ---------------------------------------------------------------------------
// PED-13 RN-16/RN-23 — o menu dedicado e o carrinho somente-leitura
// ---------------------------------------------------------------------------

/**
 * Um CONJUNTO como o MENU o lista (RN-47) — resumo, sem os itens.
 *
 * A linha é o conjunto, e não a ordem: um conjunto de três vendas é UMA linha
 * que abre UM carrinho. Listar ordem a ordem faria o cliente confirmar uma por
 * uma, que é o que RN-20 proíbe.
 */
export interface ScheduledOrderMenuItem {
  setId: string;
  referenceDate: string;
  /** RN-47 — a partir de quando o cliente pode confirmar. */
  actionableFrom: string | null;
  status: string;
  /** Quantas vendas ele vai confirmar de uma vez. */
  vendas: number;
  /** Quantas bonificações acompanham o pacote (só as vivas no SAP). */
  bonificacoes: number;
  itens: number;
  temBonificacao: boolean;
  aceito: boolean;
  cbOrderId: string | null;
  /**
   * PAG-01 — `A_VISTA` quando esta linha é um PEDIDO A PAGAR: o vendedor fechou
   * a venda no PDV e o cliente paga hoje, pelo checkout de sempre. Não há
   * aceite, e o prazo é 23:59 do dia da visita.
   *
   * ⚠️ NÃO existe uma quinta seção para ele. O conjunto à vista tem data de
   * hoje e cai em `pendentes`, que é o lugar certo — é literalmente o que o
   * cliente tem para resolver agora. "Pedido a pagar" é decisão de RENDERIZAÇÃO.
   *
   * Opcional porque o app pode estar contra um backend anterior ao campo;
   * ausente significa `A_PRAZO`.
   */
  paymentMode?: 'A_PRAZO' | 'A_VISTA';
  /**
   * RN-45 — a loja é OBRIGATÓRIA, sempre, e não só para quem tem várias. Um
   * CPF pode ter vários códigos, e sem a loja o cliente precisa caçar qual tem
   * pedido a confirmar.
   */
  clientCode: string;
  /** Por que o conjunto terminou, quando terminou. */
  terminalReasonCode: string | null;
}

/**
 * As três seções de RN-23.
 *
 * `emConsulta` existe porque o refino revogou a retenção (RN-09 a): a proposta
 * passa a chegar ao Conecta Brasal semanas antes de poder ser aceita, e o
 * cliente precisa vê-la marcada com a data em que a confirmação abre.
 */
export interface ScheduledOrderMenu {
  emConsulta: ScheduledOrderMenuItem[];
  pendentes: ScheduledOrderMenuItem[];
  /**
   * RN-18 — conjunto com cancelamento pedido e filho ainda sem confirmação do
   * SAP. Nem acionável (o vendedor já mandou cancelar) nem encerrado (uma ordem
   * ainda pode voltar viva do retry).
   */
  emCancelamento: ScheduledOrderMenuItem[];
  historico: ScheduledOrderMenuItem[];
}

/**
 * PAG-01 — uma linha da lista de pedidos à vista A PAGAR (`GET
 * /client/avista-orders`), exibida em "Meus pedidos".
 *
 * Deliberadamente MENOR que `ScheduledOrderMenuItem`: aqui não há aceite, não
 * há bonificação acompanhando, não há estado a exibir (todo item desta lista
 * está em `AGUARDANDO_PAGAMENTO`, senão não veio) e não há janela de
 * confirmação. `setId` + `clientCode` são o que `AVistaOrderReview` precisa
 * para abrir — a mesma assinatura de navegação que o menu de programados usava,
 * para a tela de revisão não mudar nada.
 */
export interface AVistaOrderListItem {
  setId: string;
  clientCode: string;
  orderApplicationId: string;
  referenceDate: string;
  itens: number;
  /**
   * PAG-01 (26/08/2026) — mesmo pipeline do preview (A-6): valor já ajustado
   * por estoque, não o total programado pelo vendedor. `null` quando o
   * catálogo do cliente falhou ao resolver do lado do servidor — o card
   * segue clicável mesmo assim; a prévia de verdade é a tela de revisão.
   */
  total: number | null;
  /** Nome do primeiro produto que sobreviveu à resolução (não o primeiro do
   * carrinho cru) — mesma razão do `itens` acima ser a contagem ajustada. */
  primeiroProduto: string | null;
}

/** Uma linha do carrinho já ajustada pelo motor de RN-16. */
export interface ScheduledOrderPreviewLine {
  sku: string | null;
  descricao: string | null;
  /** MANTIDO | REDUZIDO | REMOVIDO */
  acao: string;
  quantidadeProgramada: number;
  quantidadeAjustada: number;
  precoProgramado: number;
  precoAjustado: number;
  subtotalProgramado: number;
  subtotalAjustado: number;
  /** RN-16 — a redução cruzou um degrau de volume e o preço unitário SUBIU. */
  precoSubiu: boolean;
  /**
   * O produto resolvido, inteiro — é DELE que o aceite monta a sacola, com
   * `quantidadeAjustada`. Remontar por SKU a partir do catálogo local
   * produziria um pedido possivelmente diferente do carrinho que o cliente
   * acabou de aprovar.
   */
  produto?: any;
}

/**
 * UMA venda programada dentro do conjunto.
 *
 * As vendas NÃO são fundidas (RN-20): fusão exigiria motor de mesclagem e
 * decidir qual tabela de preço prevalece. O aceite gera uma ordem por venda, e
 * a tela reflete essa estrutura — um bloco por venda.
 */
export interface ScheduledSetSale {
  orderApplicationId: string;
  /** RN-22 — a ordem de chegada atribuída pelo CB; é ela que ordena os blocos. */
  arrivalSequence: number;
  /** Esta venda já virou pedido. Continua em tela — sumir com ela faria o
   * carrinho encolher entre um pagamento e outro —, mas fora do que há a pagar. */
  jaPedida: boolean;
  linhas: ScheduledOrderPreviewLine[];
  indisponiveis: ScheduledProposalUnavailableItem[];
  totalProgramado: number;
  totalAjustado: number;
  houveAjuste: boolean;
  /** RN-21 — a tela DEVE informar explicitamente que o preço mudou. */
  precoMudou: boolean;
  /** Nenhum item desta venda tem estoque: ela não gera ordem, e o RESTANTE do
   * conjunto segue aceitável. */
  rupturaTotal: boolean;
}

/** Uma linha de bonificação: sem preço, sem ajuste, sem cobrança. */
export interface ScheduledSetBonusLine {
  sku: string | null;
  descricao: string | null;
  /** Embalagem e tamanho. O nome do produto é `descricao` + `complemento`. */
  complemento: string | null;
  quantidade: number;
}

/**
 * UMA bonificação programada do conjunto.
 *
 * Ela NÃO está sendo aceita nesta tela — já existe como ordem no SAP desde a
 * programação. Está aqui para o cliente ver o pacote comercial inteiro que foi
 * negociado com o vendedor, que é a razão de a bonificação existir: a Brasal
 * não dá desconto em nota, dá produto.
 */
export interface ScheduledSetBonus {
  orderApplicationId: string;
  arrivalSequence: number;
  semCobranca: true;
  jaReservada: true;
  linhas: ScheduledSetBonusLine[];
  indisponiveis: ScheduledProposalUnavailableItem[];
}

export interface ScheduledSetPreview {
  setId: string;
  referenceDate: string;
  actionableFrom: string | null;
  /** `false` = ainda em consulta; a tela mostra a data de abertura, sem ação. */
  actionable: boolean;
  readOnly: true;
  vendas: ScheduledSetSale[];
  bonificacoes: ScheduledSetBonus[];
  /** Soma APENAS as vendas — e apenas as que vão nascer. */
  totalProgramado: number;
  totalAjustado: number;
  /** O total das vendas que JÁ viraram pedido. Depois do aceite é ele que a
   * tela mostra: `totalAjustado` zera, e zero se lê como pedido não registrado.
   * Opcional porque o app pode estar contra um backend anterior ao campo. */
  totalEnviado?: number;
  houveAjuste: boolean;
  precoMudou: boolean;
  /** TODAS as vendas em ruptura: não há venda possível, logo não há aceite. */
  conjuntoInviavel: boolean;
  /** Conjunto só de bonificação — nada a aceitar, e não é erro. */
  semVendas: boolean;
  /**
   * RN-24 — a data real devolvida pela função de remessa, `YYYY-MM-DD`.
   *
   * Exibida em TODO aceite, inclusive quando coincide com a programada: o
   * cliente confirma sabendo em que dia recebe. `null` quando a consulta
   * falhou — a linha some e o aceite segue, porque informar é o tratamento e
   * uma consulta que falha não pode custar a venda.
   */
  dataRealEntrega: string | null;
  /** A real difere da programada. Destaca em tela; NÃO bloqueia. */
  divergeDaProgramada: boolean;
  /**
   * RN-23 item 3 — débito vencido há mais de 15 dias. É a única validação da
   * matriz que derruba o aceite inteiro; estoque ajusta e limite de crédito só
   * restringe o meio de pagamento.
   */
  bloqueioFinanceiro: {
    motivo: string;
    diasDeTolerancia: number;
    /** Até quando dá para regularizar e ainda aceitar (D-0 do conjunto). */
    regularizarAte: string;
  } | null;
}

/**
 * PAG-01 — a SACOLA PRÉ-MONTADA do pedido à vista.
 *
 * Estrutura mais simples que a do conjunto programado, e a diferença é o ponto:
 * uma ordem só (não *n* vendas em blocos), sem bonificação, sem janela de
 * aceite e sem `jaPedida`. O que sobra é o que o cliente precisa para decidir
 * pagar.
 */
export interface AVistaOrderPreview {
  setId: string;
  orderApplicationId: string;
  paymentMode: 'A_VISTA';
  referenceDate: string;
  status: string;
  linhas: ScheduledOrderPreviewLine[];
  /** R-4 — o que caiu da cobrança, e por quê. O cliente paga VENDO. */
  indisponiveis: ScheduledProposalUnavailableItem[];
  totalProgramado: number;
  totalAjustado: number;
  houveAjuste: boolean;
  /** Menos produto com preço unitário MAIOR: a redução cruzou um degrau. */
  precoMudou: boolean;
  /** Nada sobreviveu: não há o que cobrar. */
  semItens: boolean;
  /** Fim do dia da visita, em ISO — o prazo que o vendedor anunciou no PDV. */
  pagavelAte: string;
  /** Ainda dá para pagar? Data E status. */
  pagavel: boolean;
  /** Sempre `false`: pagar é aceitar, não há passo de aceite. */
  exigeAceite: false;
  readOnly: true;
  bloqueioFinanceiro: {
    motivo: string;
    diasDeTolerancia: number;
    regularizarAte: string;
  } | null;
}

/**
 * PAG-01 — a sacola do pedido à vista.
 *
 * Endpoint PRÓPRIO, e não o do conjunto programado: aquele carrega a cascata de
 * estoque entre vendas irmãs, que não existe aqui e é a peça mais cara de
 * quebrar do PED-13. Reconstruído a cada chamada pela mesma razão do outro — o
 * ajuste de estoque é do instante da leitura, e nada é cacheado.
 */
const GetAVistaOrderPreview = async (
  clientCode: string,
  setId: string,
): Promise<AVistaOrderPreview | null> => {
  const instance = AxiosService.createProductsAxiosInstance();
  const response = await instance.get(
    `/client/avista-orders/sets/${encodeURIComponent(setId)}/preview`,
    {headers: {'client-code': clientCode}},
  );

  return response.data?.data ?? null;
};

/** RN-23 — as três seções do menu dedicado, do agrupamento inteiro. */
const GetScheduledOrderMenu = async (clientCode: string): Promise<ScheduledOrderMenu> => {
  const instance = AxiosService.createProductsAxiosInstance();
  const response = await instance.get('/client/scheduled-orders/menu', {
    headers: {'client-code': clientCode},
  });

  return (
    response.data?.data ?? {
      emConsulta: [],
      pendentes: [],
      emCancelamento: [],
      historico: [],
    }
  );
};

/**
 * PAG-01 (26/08/2026) — uma linha por pedido à vista A PAGAR da loja, para a
 * seção nova de "Meus pedidos".
 *
 * Endpoint próprio, e não uma chave a mais em `/scheduled-orders/menu`: aquele
 * carrega 90 dias de histórico de conjuntos e faz a agregação por conjunto, e
 * a tela de Pedidos abre a cada foco. Payload e latência desproporcionais para
 * uma lista que quase sempre é vazia.
 *
 * Falha NUNCA propaga para a tela: pedido à vista é uma seção acessória de
 * "Meus pedidos", e derrubar a lista de pedidos por causa dela seria trocar um
 * problema pequeno por um grande. O chamador trata com `[]`.
 */
const GetAVistaOrders = async (clientCode: string): Promise<AVistaOrderListItem[]> => {
  const instance = AxiosService.createProductsAxiosInstance();
  const response = await instance.get('/client/avista-orders', {
    headers: {'client-code': clientCode},
  });

  return response.data?.data ?? [];
};

/**
 * RN-20/RN-21 — o carrinho SOMENTE LEITURA do CONJUNTO.
 *
 * **Reconstruído a cada chamada, de propósito.** O ajuste de estoque é
 * recalculado toda vez, e a fonte é o ESTADO ATUAL do conjunto: o cliente que
 * abandonar a tela e voltar depois pode ver um carrinho diferente — por
 * acréscimo do vendedor, por cancelamento de bonificação ou por estoque. É a
 * regra, não um efeito colateral, e é por isso que nada aqui é cacheado.
 *
 * É também o que atende RN-25 sem trava nenhuma: uma ordem que chegue durante o
 * checkout não entra na sessão que já carregou, e o cliente nunca é
 * interrompido com "sua proposta mudou, comece de novo".
 */
const GetScheduledSetPreview = async (
  clientCode: string,
  setId: string,
): Promise<ScheduledSetPreview | null> => {
  const instance = AxiosService.createProductsAxiosInstance();
  const response = await instance.get(
    `/client/scheduled-orders/sets/${encodeURIComponent(setId)}/preview`,
    {headers: {'client-code': clientCode}},
  );

  return response.data?.data ?? null;
};

/**
 * A resposta do ATO DO ACEITE (RN-20/RN-21).
 *
 * O aceite gera UMA ORDEM DE VENDA POR VENDA PROGRAMADA — conjunto com três
 * vendas gera três pedidos, sem fusão de carrinhos. O servidor faz o laço; o
 * app recebe o resultado POR ORDEM.
 */
export interface ScheduledSetAcceptResult {
  setId: string;
  setStatus: string;
  /**
   * As vendas A PAGAR, NA ORDEM DE PAGAMENTO — que é a ordem de criação, a
   * mesma que o motor usou para repartir o estoque. Reordenar aqui faria o
   * cliente pagar uma venda com quantidades calculadas para outra posição da
   * fila.
   *
   * O pedido NÃO nasce no aceite: nasce no checkout, uma venda por vez, pelos
   * três meios que já existem. Em boleto, na confirmação do pedido; em PIX e
   * cartão, na confirmação do PAGAMENTO (RN-19 regra 5).
   */
  aPagar: Array<{orderApplicationId: string; arrivalSequence: number}>;
  /** Vendas encerradas por RUPTURA TOTAL — informativo, o vendedor já foi
   * avisado pelo servidor. O restante do conjunto foi aceito. */
  encerradasPorRuptura: Array<{orderApplicationId: string; reasonCode: string}>;
}

/**
 * RN-21 — o ATO DO ACEITE do conjunto.
 *
 * Chamado UMA vez, quando o cliente toca em aceitar: é aqui que o servidor
 * reparte o estoque entre as vendas na ordem de criação, encerra as que caíram
 * em ruptura total e avisa o vendedor. Só depois o app entra no checkout, venda
 * a venda.
 *
 * Fazer isso no servidor, e não deduzir do preview, é o que garante que o
 * encerramento por ruptura aconteça UMA vez, no ato do aceite — e não a cada
 * leitura do carrinho, onde uma falta de estoque de dez minutos mataria uma
 * venda que estaria disponível de tarde.
 */
const AcceptScheduledSet = async (
  clientCode: string,
  setId: string,
): Promise<ScheduledSetAcceptResult | null> => {
  const instance = AxiosService.createProductsAxiosInstance();
  const response = await instance.post(
    `/client/scheduled-orders/sets/${encodeURIComponent(setId)}/accept`,
    {},
    {headers: {'client-code': clientCode}},
  );

  return response.data?.data ?? null;
};

export default {
  GetScheduledProposal,
  GetScheduledOrderMenu,
  GetAVistaOrders,
  GetScheduledSetPreview,
  GetAVistaOrderPreview,
  AcceptScheduledSet,
};
